/**
 * Paste Service
 * Consolidated service for paste and clipboard operations
 * Handles paste operations, tag restoration from clipboard, and clipboard copy/cut operations
 */

import { inject, Injectable } from '@angular/core'
import {
  DOM_ELEMENT_NAMES,
  TAG_EDITOR_COLORS,
  TAG_EDITOR_STRINGS,
  TAG_EDITOR_THRESHOLDS,
  TAG_EDITOR_VALUES,
} from '../tag-editor.constants'
import { TagEditorTreeNode } from '../tag-editor-tree-node'
import { DebugLoggingService } from './debug-logging.service'
import { TagManagerService } from './tag-manager.service'

@Injectable({ providedIn: 'root' })
export class PasteService {
  private readonly debugLoggingService = inject(DebugLoggingService)
  private readonly tagManagerService = inject(TagManagerService)

  /**
   * Checks if pasted plain text matches any existing tags and creates new tag if needed
   */
  checkAndCreateTagFromPlainText(
    pastedText: string,
    editorElement: HTMLElement,
    treeNodes: ReadonlyArray<TagEditorTreeNode>,
    generateTagId: () => string,
    onParseAndUpdateTags: () => void,
  ): void {
    this.debugLoggingService.logWithService('PasteService', 'checkAndCreateTagFromPlainText called:', {
      pastedTextLength: pastedText.length,
      pastedTextPreview: pastedText.substring(0, TAG_EDITOR_VALUES.CONTENT_PREVIEW_LENGTH),
    })

    const newElements: NodeListOf<Element> = editorElement.querySelectorAll(
      'span:not([data-tag-id]), u:not([data-tag-id])',
    )
    this.debugLoggingService.logWithService(
      'PasteService',
      'Found new elements without data-tag-id:',
      newElements.length,
    )

    let createdCount = 0

    newElements.forEach((element: Element, elementIndex: number) => {
      const htmlElement: HTMLElement = element as HTMLElement
      const elementText: string = htmlElement.textContent?.trim() || ''

      this.debugLoggingService.logWithService('PasteService', `Checking element ${elementIndex}:`, {
        elementText: elementText.substring(0, TAG_EDITOR_VALUES.CONTENT_PREVIEW_LENGTH),
        pastedText: pastedText.substring(0, TAG_EDITOR_VALUES.CONTENT_PREVIEW_LENGTH),
        similarity: this.tagManagerService.calculateTextSimilarity(elementText, pastedText),
      })

      if (
        this.tagManagerService.calculateTextSimilarity(elementText, pastedText) >
        TAG_EDITOR_THRESHOLDS.TEXT_SIMILARITY_THRESHOLD
      ) {
        const matchingNode: TagEditorTreeNode | null = this.findMatchingTreeNode(elementText, treeNodes)

        if (matchingNode) {
          this.debugLoggingService.logWithService(
            'PasteService',
            `Found matching tree node for element ${elementIndex}:`,
            {
              nodeLabel: matchingNode.label,
              nodeKey: matchingNode.key,
            },
          )

          const newTagId: string = generateTagId()
          this.tagManagerService.applyTagAttributesToElement(
            htmlElement,
            newTagId,
            matchingNode.label,
            TAG_EDITOR_COLORS.DEFAULT_TAG_COLOR,
            matchingNode.data?.categoryName,
            matchingNode.data?.categoryId?.toString(),
          )

          createdCount++
          this.debugLoggingService.logWithService('PasteService', `Created new tag from plain text:`, {
            newId: newTagId,
            label: matchingNode.label,
            elementText: elementText.substring(0, TAG_EDITOR_VALUES.CONTENT_PREVIEW_LENGTH),
          })
        } else {
          this.debugLoggingService.logWithService(
            'PasteService',
            `No matching tree node found for element ${elementIndex}`,
          )
        }
      }
    })

    if (createdCount > 0) {
      this.debugLoggingService.logWithService('PasteService', 'Created tags from plain text:', { createdCount })
      onParseAndUpdateTags()
    } else {
      this.debugLoggingService.logWithService('PasteService', 'No tags created from plain text')
    }
  }

  /**
   * Handles cut operations with clipboard preservation
   */
  handleCutOperation(
    onParseAndUpdateTags: () => void,
    onEmitChange: (content: string) => void,
    getEditorHTML: () => string,
  ): void {
    const selection: Selection | null = window.getSelection()
    if (!selection || selection.rangeCount === 0) {
      this.debugLoggingService.logWithService('PasteService', 'No selection for cut operation')
      return
    }

    const range: Range = selection.getRangeAt(0)
    if (range.collapsed) {
      this.debugLoggingService.logWithService('PasteService', 'No content selected for cut operation')
      return
    }

    const selectedContent: DocumentFragment = range.cloneContents()
    const tempDiv: HTMLElement = document.createElement(DOM_ELEMENT_NAMES.DIV)
    tempDiv.appendChild(selectedContent)
    const htmlContent: string = tempDiv.innerHTML

    this.debugLoggingService.logWithService(
      'PasteService',
      'Cutting content:',
      htmlContent.substring(0, TAG_EDITOR_VALUES.CONTENT_PREVIEW_LENGTH),
    )

    this.copyContentToClipboard(htmlContent)

    range.deleteContents()

    onParseAndUpdateTags()
    onEmitChange(getEditorHTML())
  }

  /**
   * Copies content to clipboard using modern Clipboard API
   */
  copyContentToClipboard(htmlContent: string): void {
    if (!navigator.clipboard || !window.isSecureContext) {
      this.debugLoggingService.logWithService(
        'PasteService',
        'Clipboard API not available - clipboard operations require secure context',
      )
      return
    }

    const tempDiv: HTMLElement = document.createElement(DOM_ELEMENT_NAMES.DIV)
    tempDiv.innerHTML = htmlContent
    const plainText: string = tempDiv.textContent || tempDiv.innerText || TAG_EDITOR_STRINGS.EMPTY_STRING

    navigator.clipboard
      .write([
        new ClipboardItem({
          [TAG_EDITOR_STRINGS.TEXT_HTML_MIME_TYPE]: new Blob([htmlContent], {
            type: TAG_EDITOR_STRINGS.TEXT_HTML_MIME_TYPE,
          }),
          [TAG_EDITOR_STRINGS.TEXT_PLAIN_MIME_TYPE]: new Blob([plainText], {
            type: TAG_EDITOR_STRINGS.TEXT_PLAIN_MIME_TYPE,
          }),
        }),
      ])
      .catch((error: unknown) => {
        this.debugLoggingService.logWithService('PasteService', 'Clipboard API failed:', error)
      })
  }

  /**
   * Copies HTML content as plain text to clipboard for debugging
   */
  public copyHtmlAsPlainText(htmlContent: string): void {
    if (!navigator.clipboard || !window.isSecureContext) {
      this.debugLoggingService.logWithService(
        'PasteService',
        'Clipboard API not available - clipboard operations require secure context',
      )
      return
    }

    navigator.clipboard.writeText(htmlContent).catch((error: unknown) => {
      this.debugLoggingService.logWithService('PasteService', 'Clipboard API failed:', error)
    })
  }

  // ========== Private Helper Methods ==========

  private findMatchingTreeNode(text: string, treeNodes: ReadonlyArray<TagEditorTreeNode>): TagEditorTreeNode | null {
    const normalizedText: string = text.trim().toLowerCase()

    for (const node of treeNodes) {
      if (node.label.toLowerCase() === normalizedText) {
        return node
      }

      if (node.children) {
        const childMatch: TagEditorTreeNode | null = this.findMatchingTreeNodeInChildren(node.children, normalizedText)
        if (childMatch) {
          return childMatch
        }
      }
    }

    return null
  }

  private findMatchingTreeNodeInChildren(
    children: ReadonlyArray<TagEditorTreeNode>,
    normalizedText: string,
  ): TagEditorTreeNode | null {
    for (const child of children) {
      if (child.label.toLowerCase() === normalizedText) {
        return child
      }

      if (child.children) {
        const grandChildMatch: TagEditorTreeNode | null = this.findMatchingTreeNodeInChildren(
          child.children,
          normalizedText,
        )
        if (grandChildMatch) {
          return grandChildMatch
        }
      }
    }

    return null
  }
}
